import { useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  api,
  exportBuchhaltungUrl,
  exportErschwerniszulageUrl,
  fetchZeitbuchungen,
  toggleBuchung,
  type Zeitbuchung,
  type ZeitbuchungenFilter,
} from "@/lib/api"
import { EmployeePicker, useEmployeeLabel, type EmployeeOption } from "@/components/EmployeePicker"
import { Input, Select } from "@/components/ui"
import { DataGrid, type ColumnDef } from "@/components/DataGrid"
import { useToast, fehlerNachricht } from "@/lib/toast"

/** Buchungs-Historie mit Filtern (F2 „Meine Zeitbuchungen") + Exporte (AP-3.8). */
export function ZeitbuchungenHistoriePage() {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [filter, setFilter] = useState<ZeitbuchungenFilter>({})
  const [jahr, setJahr] = useState<number>(new Date().getFullYear())
  const [monat, setMonat] = useState<number>(new Date().getMonth() + 1)

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["zeitbuchungen", filter],
    queryFn: () => fetchZeitbuchungen(filter),
  })

  const toggleMutation = useMutation({
    mutationFn: toggleBuchung,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["zeitbuchungen"] })
      toast.success("Buchungsstatus aktualisiert.")
    },
    onError: (err) =>
      toast.error(fehlerNachricht(err, "Buchungsstatus konnte nicht geändert werden.")),
  })

  // Mitarbeiter-Liste für die Namens-Auflösung in Sortierung/Schnellfilter
  // (gleicher Query-Key wie useEmployeeLabel → geteilter Cache, UX_KONZEPT.md §2.4).
  const { data: employees } = useQuery({
    queryKey: ["employees", "all"],
    queryFn: async () => {
      const resp = await api.get<EmployeeOption[]>("/employees", { params: { nur_aktive: false } })
      return resp.data
    },
    staleTime: 5 * 60_000,
  })
  const mitarbeiterLabel = (employeeId: string) => {
    const e = employees?.find((x) => x.id === employeeId)
    return e ? (e.kuerzel ? `${e.name} (${e.kuerzel})` : e.name) : employeeId
  }

  // Grid-Spalten: header als String, damit die Spalten-Auswahl ihn anzeigen kann
  const spalten = useMemo<ColumnDef<Zeitbuchung>[]>(
    () => [
      {
        id: "datum",
        header: "Datum",
        accessorFn: (b) => b.datum,
        cell: ({ row }) => new Date(row.original.datum).toLocaleDateString("de-DE"),
      },
      {
        id: "mitarbeiter",
        header: "Mitarbeiter",
        accessorFn: (b) => mitarbeiterLabel(b.employeeId),
        cell: ({ row }) => (
          <span className="text-slate-900">
            <ZeitbuchungMitarbeiterZelle employeeId={row.original.employeeId} />
          </span>
        ),
      },
      {
        id: "leistung",
        header: "Leistung",
        accessorFn: (b) => b.leistungAnzeige ?? "",
        cell: ({ row }) => row.original.leistungAnzeige ?? "—",
      },
      {
        id: "stunden",
        header: "Stunden",
        accessorFn: (b) => b.stunden,
        cell: ({ row }) =>
          `${new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2 }).format(row.original.stunden)} h`,
      },
      {
        id: "navisionNr",
        header: "NavisionNr",
        accessorFn: (b) => b.navisionNr ?? "",
        cell: ({ row }) => <span className="font-mono">{row.original.navisionNr ?? "—"}</span>,
      },
      {
        id: "timetabId",
        header: "TimeTab-ID",
        accessorFn: (b) => b.timetabId ?? "",
        cell: ({ row }) => (
          <span className="font-mono text-xs">{row.original.timetabId ?? "—"}</span>
        ),
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (b) => (b.gebucht ? "gebucht" : "offen"),
        cell: ({ row }) => (
          <button
            onClick={() => toggleMutation.mutate(row.original.id)}
            className={`rounded-full px-2 py-0.5 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1 ${
              row.original.gebucht ? "bg-green-100 text-green-800" : "bg-slate-200 text-slate-600"
            }`}
          >
            {row.original.gebucht ? "gebucht" : "offen"}
          </button>
        ),
      },
    ],
    // mitarbeiterLabel hängt an den Query-Daten; toggleMutation ist stabil
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [employees],
  )

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">Zeitbuchungen</h1>
        <p className="mt-1 text-sm text-slate-600">
          Buchungs-Historie (03-F) mit Filtern — Buchungssperre (03-F3) und Exporte (F6/F7).
        </p>
      </header>

      <div className="mb-4 flex flex-wrap items-end gap-4 rounded-lg border border-slate-200 bg-white p-4">
        <div className="w-64">
          <EmployeePicker
            value={filter.employeeId ?? null}
            onChange={(id) => setFilter((f) => ({ ...f, employeeId: id ?? undefined }))}
            label="Mitarbeiter (Filter)"
          />
        </div>
        <label className="text-sm">
          <span className="mb-1 block text-slate-700">Von</span>
          <Input
            type="date"
            value={filter.von ?? ""}
            onChange={(e) => setFilter((f) => ({ ...f, von: e.target.value || undefined }))}
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-slate-700">Bis</span>
          <Input
            type="date"
            value={filter.bis ?? ""}
            onChange={(e) => setFilter((f) => ({ ...f, bis: e.target.value || undefined }))}
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-slate-700">Gebucht</span>
          <Select
            value={filter.gebucht === undefined ? "" : String(filter.gebucht)}
            onChange={(e) =>
              setFilter((f) => ({
                ...f,
                gebucht: e.target.value === "" ? undefined : e.target.value === "true",
              }))
            }
          >
            <option value="">alle</option>
            <option value="true">gebucht</option>
            <option value="false">offen</option>
          </Select>
        </label>
      </div>

      <div className="mb-6 flex flex-wrap items-end gap-4 rounded-lg border border-slate-200 bg-white p-4">
        <label className="text-sm">
          <span className="mb-1 block text-slate-700">Jahr (Export)</span>
          <Input
            type="number"
            value={jahr}
            onChange={(e) => setJahr(Number(e.target.value))}
            className="w-24"
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-slate-700">Monat (Export)</span>
          <Input
            type="number"
            min={1}
            max={12}
            value={monat}
            onChange={(e) => setMonat(Number(e.target.value))}
            className="w-20"
          />
        </label>
        <a
          href={exportBuchhaltungUrl(jahr, monat)}
          className="inline-flex h-9 items-center gap-2 rounded-md bg-slate-900 px-4 text-sm font-medium text-white transition-colors hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
        >
          Buchhaltungsexport (F7)
        </a>
        <a
          href={exportErschwerniszulageUrl(jahr, monat)}
          className="inline-flex h-9 items-center gap-2 rounded-md bg-slate-900 px-4 text-sm font-medium text-white transition-colors hover:bg-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
        >
          Erschwerniszulagen-Export (F6)
        </a>
      </div>

      {isLoading && (
        <div className="animate-pulse space-y-2" aria-label="Lade Zeitbuchungen …">
          <div className="h-8 rounded-md bg-slate-100" />
          <div className="h-8 rounded-md bg-slate-100" />
          <div className="h-8 rounded-md bg-slate-100" />
        </div>
      )}
      {isError && (
        <p className="text-sm text-red-700">
          Zeitbuchungen konnten nicht geladen werden.{" "}
          <button
            type="button"
            onClick={() => refetch()}
            className="rounded-md underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
          >
            Erneut versuchen
          </button>
        </p>
      )}

      {data && (
        <>
          <DataGrid
            columns={spalten}
            data={data}
            storageKey="zeitbuchungen-historie"
            emptyMessage="Noch keine Zeitbuchungen für diese Filter."
          />
          {data.length === 0 && (
            <p className="mt-2 text-center text-sm">
              <a
                href="/timetab"
                className="inline-block rounded-md text-slate-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
              >
                + Zeit erfassen
              </a>
            </p>
          )}
        </>
      )}
    </div>
  )
}

/** Zellen-Auflösung employee_id → „Name (Kürzel)" über den employees-Cache
 * (Muster UX_KONZEPT.md §2.4); unauflösbare IDs bleiben als <code>-ID sichtbar. */
function ZeitbuchungMitarbeiterZelle({ employeeId }: { employeeId: string }) {
  const label = useEmployeeLabel(employeeId)
  return label ? (
    <span>{label}</span>
  ) : (
    <code title="Mitarbeiter nicht (mehr) im Cache" className="text-xs text-slate-500">
      {employeeId}
    </code>
  )
}
